import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { Trash2Icon } from 'lucide-react';
import { expiryAlertWindow, isExpired } from './pharmacyUtils';
export function ExpiredMedicines() {
  const {
    currentUser,
    medicineBatches,
    medicines,
    recordStockMovement
  } = useData();
  const [message, setMessage] = useState('');
  const manager = currentUser?.role === 'Pharmacy Manager';
  const groups = useMemo(() => ({
    expired: medicineBatches.filter(batch => expiryAlertWindow(batch) === 'Expired'),
    days30: medicineBatches.filter(batch => expiryAlertWindow(batch) === '30 Days'),
    days60: medicineBatches.filter(batch => expiryAlertWindow(batch) === '60 Days'),
    days90: medicineBatches.filter(batch => expiryAlertWindow(batch) === '90 Days')
  }), [medicineBatches]);
  const dispose = async (batchId: string) => {
    const batch = medicineBatches.find(item => item.id === batchId);
    if (!batch || !confirm(`Dispose ${batch.quantityRemaining} blocked units from batch ${batch.batchNumber}?`)) return;
    const result = await recordStockMovement({
      medicineId: batch.medicineId,
      batchId: batch.id,
      quantityDelta: -batch.quantityRemaining,
      type: 'Expired Disposal',
      referenceType: 'Expiry',
      referenceId: batch.id,
      reason: 'Expired',
      notes: `Expired batch ${batch.batchNumber} disposed from blocked inventory.`
    });
    setMessage(result.ok ? 'Expired stock disposed and permanently recorded in movement history.' : result.error || 'Unable to dispose stock.');
  };
  return <div className="space-y-6" data-id="element-4451">
      <div data-id="element-4452">
        <h2 className="text-xl font-bold text-slate-950" data-id="element-4453">Expiry Management</h2>
        <p className="mt-1 text-sm text-slate-500" data-id="element-4454">
          Expiry alerts update live at 90, 60, and 30 days. Expired batches are
          blocked from dispensing until disposal.
        </p>
      </div>
      {message && <p role="status" className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700" data-id="element-4455">
          {message}
        </p>}
      <BatchTable title="Expired — dispensing blocked" tone="red" batches={groups.expired} medicines={medicines} action={manager ? dispose : undefined} data-id="element-4456" />
      {!manager && groups.expired.length > 0 && <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-id="element-4457">
          Expired inventory remains blocked. A Pharmacy Manager must authorise
          disposal.
        </p>}
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3" data-id="element-4458">
        <BatchTable title="Within 30 days" tone="orange" batches={groups.days30} medicines={medicines} data-id="element-4459" />
        <BatchTable title="Within 60 days" tone="amber" batches={groups.days60} medicines={medicines} data-id="element-4460" />
        <BatchTable title="Within 90 days" tone="amber" batches={groups.days90} medicines={medicines} data-id="element-4461" />
      </div>
    </div>;
}
function BatchTable({
  title,
  tone,
  batches,
  medicines,
  action
}: {
  title: string;
  tone: 'red' | 'orange' | 'amber';
  batches: ReturnType<typeof useData>['medicineBatches'];
  medicines: ReturnType<typeof useData>['medicines'];
  action?: (batchId: string) => void;
}) {
  const toneClass = tone === 'red' ? 'border-red-200 bg-red-50 text-red-800' : tone === 'orange' ? 'border-orange-200 bg-orange-50 text-orange-800' : 'border-amber-200 bg-amber-50 text-amber-800';
  return <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-4462">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4" data-id="element-4463">
        <div data-id="element-4464">
          <h3 className="font-bold text-slate-900" data-id="element-4465">{title}</h3>
          <p className="mt-0.5 text-sm text-slate-500" data-id="element-4466">
            {batches.length} active batch{batches.length === 1 ? '' : 'es'}{' '}
            require attention
          </p>
        </div>
        <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${toneClass}`} data-id="element-4467">
          {batches.reduce((sum, batch) => sum + batch.quantityRemaining, 0)}{' '}
          units
        </span>
      </div>
      <div className="overflow-x-auto" data-id="element-4468">
        <table className="min-w-full divide-y divide-slate-100" data-id="element-4469">
          <thead className="bg-slate-50" data-id="element-4470">
            <tr data-id="element-4471">
              {['Medicine', 'Batch', 'Expiry date', 'Blocked / available units', ...(action ? ['Action'] : [])].map(heading => <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-4472">
                  {heading}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-4473">
            {batches.length ? batches.map(batch => {
            const medicine = medicines.find(item => item.id === batch.medicineId);
            const blocked = isExpired(batch);
            return <tr key={batch.id} data-id="element-4474">
                    <td className="px-5 py-3.5 font-semibold text-slate-900" data-id="element-4475">
                      {medicine?.name || 'Unknown medicine'}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4476">
                      {batch.batchNumber}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-600" data-id="element-4477">
                      {new Date(`${batch.expiryDate}T00:00:00`).toLocaleDateString()}
                    </td>
                    <td className={`px-5 py-3.5 text-sm font-semibold ${blocked ? 'text-red-700' : 'text-slate-900'}`} data-id="element-4478">
                      {batch.quantityRemaining}{' '}
                      {blocked ? 'blocked' : 'available'}
                    </td>
                    {action && <td className="px-5 py-3.5 text-right" data-id="element-4479">
                        <Button size="sm" variant="danger" onClick={() => action(batch.id)} data-id="element-4480">
                          <Trash2Icon className="mr-1.5 h-4 w-4" data-id="element-4481" /> Dispose
                        </Button>
                      </td>}
                  </tr>;
          }) : <tr data-id="element-4482">
                <td colSpan={action ? 5 : 4} className="px-5 py-10 text-center text-sm text-slate-500" data-id="element-4483">
                  No batches in this alert window.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </section>;
}